import React, { useState, useEffect, useRef } from "react"
import { GetCategory } from "../api"
import BrowserArticle from "./BrowserArticle"
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline"
import Typography from "aldoo-ra/Typography"
import TextBundle from "aldoo-ra/CMS/text-bundle"

/**
 * BrowserCategory Component
 * Updated to work with preloaded TOC data with fixed toggle behavior
 * and no flickering for nested categories
 * Now with status filtering to only show published content
 */
const BrowserCategory = ({
  category,
  level = 0,
  parentPath = "",
  onArticleSelect,
  onCategorySelect,
  expandedCategories = {},
  currentPath = {},
  fullPath = "",
  isMobile = false,
  preloadedData = false, // Flag to indicate if we're using preloaded TOC data
}) => {
  const texts = TextBundle({ id: "help-center" })

  // If this category is in draft status, don't render it
  if (category.status === "draft" || category.content?.status === "draft") {
    return null
  }

  // Calculate the full path for this category
  const categoryFullPath = parentPath
    ? `${parentPath}/${category.slug}`
    : category.slug

  // For preloaded data, immediately set up the category data
  // This prevents flicker on toggle since data is always available
  const initialCategoryData = preloadedData
    ? {
        content: {
          categories: category.categories || [],
          articles: category.articles || [],
        },
      }
    : null

  const [categoryData, setCategoryData] = useState(initialCategoryData)
  const [isLoading, setIsLoading] = useState(false)
  const [hasLoadedOnce, setHasLoadedOnce] = useState(preloadedData)

  // Use ref to track category ID to avoid unnecessary effect triggers
  const categoryIdRef = useRef(category._id)

  // Parse exact path from currentPath (URL-derived)
  const exactPath = currentPath.categoryPath || ""
  const articleSlug = currentPath.type === "article" ? currentPath.slug : null

  // More precise calculation of exactPath match using path parts
  const exactPathParts = exactPath ? exactPath.split("/") : []
  const currentPathParts = categoryFullPath ? categoryFullPath.split("/") : []

  // A category is in the exact path if it's a prefix of the path parts
  const isInExactPath =
    currentPathParts.length > 0 &&
    exactPathParts.length >= currentPathParts.length &&
    currentPathParts.every((part, index) => part === exactPathParts[index])

  // Check if this category contains the active article
  const containsActiveArticle =
    currentPath.type === "article" &&
    currentPath.categoryPath === categoryFullPath

  // Calculate if this is the exact category being viewed
  const isExactCategoryMatch =
    currentPath.type === "category" &&
    currentPath.categoryPath === categoryFullPath

  // Set isActive if directly active or should be highlighted
  const isActive = isExactCategoryMatch
  const isHighlighted = isInExactPath

  // SIMPLIFIED EXPANSION LOGIC:
  // First check for direct toggle state in expandedCategories
  const hasManualToggle = categoryFullPath in expandedCategories

  // If not in expandedCategories (never toggled), auto-expand if in current path
  const isAutoExpanded = isInExactPath || containsActiveArticle

  // Determine final expanded state:
  // 1. If manually toggled, use that state
  // 2. Otherwise, auto-expand if in the current path or contains active article
  const isExpanded = hasManualToggle
    ? expandedCategories[categoryFullPath]
    : isAutoExpanded

  // Effect to update local data when category ID changes
  useEffect(() => {
    if (category._id !== categoryIdRef.current) {
      categoryIdRef.current = category._id

      // If preloaded data, immediately update the category data
      if (preloadedData) {
        setCategoryData({
          content: {
            categories: category.categories || [],
            articles: category.articles || [],
          },
        })
        setHasLoadedOnce(true)
      } else {
        setHasLoadedOnce(false)
        setCategoryData(null)
      }
    }
  }, [category._id, category.categories, category.articles, preloadedData])

  // Handle API fetching (only for non-preloaded data)
  useEffect(() => {
    // Skip if using preloaded data or already loaded
    if (preloadedData || (hasLoadedOnce && categoryData)) {
      return
    }

    // Skip if not expanded
    if (!isExpanded) {
      return
    }

    // API fetching logic for non-preloaded data
    const fetchCategoryData = async () => {
      if (!hasLoadedOnce) {
        setIsLoading(true)
        try {
          const data = await GetCategory(category.slug)
          setCategoryData(data)
          setHasLoadedOnce(true)
        } catch (error) {
          console.error("Error fetching category:", error)
        } finally {
          setIsLoading(false)
        }
      }
    }

    fetchCategoryData()
  }, [isExpanded, hasLoadedOnce, category.slug, categoryData, preloadedData])

  const handleToggle = (e) => {
    e.stopPropagation()

    if (onCategorySelect) {
      onCategorySelect(category.slug, parentPath)
    }
  }

  const indentationStyle = {
    paddingLeft: `${level * 20}px`,
    borderLeft: level > 2 ? "1px solid #e5e7eb" : "none",
    marginLeft: level > 0 ? "10px" : "0",
  }

  // With preloaded data, we always have content to render
  const hasContent = preloadedData || categoryData || isLoading

  // Filter subcategories based on status
  const getFilteredSubcategories = () => {
    const subcategories = preloadedData
      ? category.categories
      : categoryData?.content?.categories

    if (!subcategories || subcategories.length === 0) return []

    return subcategories.filter(
      (subcat) =>
        subcat.status !== "draft" && subcat.content?.status !== "draft"
    )
  }

  // Filter articles based on status
  const getFilteredArticles = () => {
    const articles = preloadedData
      ? category.articles
      : categoryData?.content?.articles

    if (!articles || articles.length === 0) return []

    return articles.filter(
      (article) =>
        article.status !== "draft" && article.content?.status !== "draft"
    )
  }

  const filteredSubcategories = getFilteredSubcategories()
  const filteredArticles = getFilteredArticles()

  return (
    <div>
      <div
        className={`flex items-center py-2 cursor-pointer ${
          isHighlighted ? "text-primary" : "text-text"
        }`}
        onClick={handleToggle}
        style={indentationStyle}
      >
        {/* Subcategory label */}
        <Typography
          variant="body1_accent"
          className={`text-left my-0 ${
            isActive
              ? "!text-primary"
              : isHighlighted
              ? "!text-primary"
              : "!text-text"
          }`}
        >
          {category.label}
        </Typography>

        {/* Chevron icon */}
        <span
          className={`ml-2 ${
            isActive
              ? "text-primary"
              : isHighlighted
              ? "text-primary"
              : "text-gray_700"
          }`}
        >
          {isExpanded ? (
            <ChevronUpIcon className="w-5 h-5" />
          ) : (
            <ChevronDownIcon className="w-5 h-5" />
          )}
        </span>

        {isLoading && (
          <Typography variant="txt1_dim" className="ml-2">
            {texts?.loading || "Loading..."}
          </Typography>
        )}
      </div>

      {/* Content container with max-height transition to prevent jumping */}
      <div
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{
          maxHeight: isExpanded ? "2000px" : "0px", // Using a large max-height when expanded
          opacity: isExpanded ? 1 : 0,
        }}
      >
        {hasContent && (
          <div>
            {/* Render subcategories if available - only published ones */}
            {filteredSubcategories.length > 0 && (
              <div>
                {filteredSubcategories.map((subCategory) => (
                  <BrowserCategory
                    key={subCategory._id}
                    category={subCategory}
                    level={level + 1}
                    parentPath={categoryFullPath}
                    onArticleSelect={onArticleSelect}
                    onCategorySelect={onCategorySelect}
                    expandedCategories={expandedCategories}
                    currentPath={currentPath}
                    fullPath={`${fullPath}/${category.slug}`}
                    isMobile={isMobile}
                    preloadedData={preloadedData} // Pass down the preloadedData flag
                  />
                ))}
              </div>
            )}

            {/* Render articles if available - only published ones */}
            {filteredArticles.length > 0 && (
              <div className="articles">
                {filteredArticles.map((article) => {
                  // Check if this article is precisely the one in the URL
                  const isArticleActive =
                    currentPath.type === "article" &&
                    articleSlug === article.slug &&
                    // Must be in the exact category path
                    isInExactPath

                  return (
                    <BrowserArticle
                      key={article._id}
                      article={article}
                      parentPath={categoryFullPath}
                      level={level}
                      onArticleSelect={onArticleSelect}
                      isActive={isArticleActive}
                      currentPath={currentPath}
                    />
                  )
                })}
              </div>
            )}

            {/* Show message if no content or all content is draft */}
            {filteredSubcategories.length === 0 &&
              filteredArticles.length === 0 && (
                <div
                  style={{
                    ...indentationStyle,
                    paddingLeft: `${(level + 1) * 20}px`,
                  }}
                  className="py-2 text-gray_700"
                >
                  {texts?.empty_category || "No content in this category"}
                </div>
              )}
          </div>
        )}
      </div>
    </div>
  )
}

export default BrowserCategory
